import React, { useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Button, Card, Checkbox, Flex, List, Modal, Typography } from "antd";
import { DeleteOutlined, FileProtectOutlined } from "@ant-design/icons";
import {
  addGroupMailPropertiesIdentitiesAllowedToPost,
  deleteGroupMailPropertiesIdentitiesAllowedToPost,
  addGroupMailPropertiesGroupsAllowedToPost,
  deleteGroupMailPropertiesGroupsAllowedToPost,
} from "../../../actions/api";
import { ContentTable } from "@authzsvc/common-react-components";
import TooltipIcon from "../../common/TooltipIcon";
import LabeledSwitch from "../../common/LabeledSwitch";
import AddMemberSearchModal from "../../common/AddMemberSearchModal";
import { errorAction, types } from "@authzsvc/api-lib";
import { getMemberTypeLabel } from "../../../constants";
import {
  showErrorNotification,
  showInfoNotification,
  showSuccessNotification,
} from "../../../util";

const MailPostingRestrictions = ({
  groupMailPropertiesId,
  identitiesAllowedToPost,
  groupsAllowedToPost,
  selectedGroup,
  allowAuthenticatedMessagesOnly,
  openPosting,
  canModifyGroup,
  updateGroupMailProperties,
  fetchGroupMailProperties,
  setLoading,
}) => {
  const dispatch = useDispatch();
  const [cachedRestrictSenders, setCachedRestrictSenders] = useState(null);
  const [cachedCustomSenders, setCachedCustomSenders] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [itemsToRemove, setItemsToRemove] = useState([]);

  const restrictSendersSwitchValue = !openPosting;
  const groupMembersCheckboxState =
    selectedGroup.id === selectedGroup.administratorsId;
  const groupMembersCheckboxValue = groupsAllowedToPost?.some(
    (groupAllowedToPost) => groupAllowedToPost?.id === selectedGroup.id
  );
  const customGroupsAllowedToPost =
    groupsAllowedToPost?.filter((group) => group.id !== selectedGroup.id) || [];
  const dataSource = [
    ...identitiesAllowedToPost
      ?.filter((identity) => identity.id !== selectedGroup.ownerId)
      .map((identity) => ({
        key: identity.id,
        displayName: `${identity.displayName} ${
          identity.upn ? `(${identity.upn})` : ""
        }`,
        type: getMemberTypeLabel({ memberIdentity: identity }, true),
      })),
    ...groupsAllowedToPost
      ?.filter(
        (group) =>
          group.id !== selectedGroup.id &&
          group.id !== selectedGroup.administratorsId
      )
      .map((group) => ({
        key: group.id,
        displayName: group.groupIdentifier,
        type: getMemberTypeLabel({ memberGroup: group }, true),
      })),
  ];
  const customSendersCheckboxValue =
    restrictSendersSwitchValue && (dataSource.length > 0 || isAddModalOpen);
  const hasSelections = selectedRows.length > 0;
  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (selectedRowKeys) => {
      setSelectedRows(selectedRowKeys);
    },
  };
  const tableColumns = [
    {
      title: "Identifier",
      dataIndex: "displayName",
    },
    {
      title: "Type",
      dataIndex: "type",
    },
  ];
  const nonAddableMembers = [
    {
      type: "Identity",
      identifier: selectedGroup.ownerId,
      message:
        "The group owner is already included by default and cannot be added as a custom sender.",
    },
    {
      type: "Group",
      identifier: selectedGroup.administratorsId,
      message:
        "The administrators group is already included by default and cannot be added as a custom sender.",
    },
    {
      type: "Group",
      identifier: selectedGroup.id,
      message:
        "The group itself is included through the 'Group Members' checkbox and cannot be added as a custom sender.",
    },
  ];

  const getCurrentCustomSenders = () => ({
    identitiesAllowedToPost: [...identitiesAllowedToPost],
    groupsAllowedToPost: [...customGroupsAllowedToPost],
  });

  const getCurrentAllSenders = () => ({
    identitiesAllowedToPost: [...identitiesAllowedToPost],
    groupsAllowedToPost: [...groupsAllowedToPost],
  });

  const handleRestrictAuthenticatedSendersSwitchChange = (checked) => {
    updateGroupMailProperties({
      allowAuthenticatedMessagesOnly: checked,
    });
  };

  const dispatchCustomSendersAdd = async (
    { identities = [], groups = [] },
    suppressNotificationsAndRefresh = false
  ) => {
    const dispatchActions = [];
    if (identities.length) {
      dispatchActions.push(
        dispatch(
          addGroupMailPropertiesIdentitiesAllowedToPost(
            groupMailPropertiesId,
            identities
          )
        )
      );
    }
    if (groups.length) {
      dispatchActions.push(
        dispatch(
          addGroupMailPropertiesGroupsAllowedToPost(
            groupMailPropertiesId,
            groups
          )
        )
      );
    }
    if (dispatchActions.length) {
      return Promise.all(dispatchActions)
        .then((response) => {
          const isErrorAction =
            response?.[0].type ===
              errorAction(
                types.ADD_GROUP_MAIL_PROPERTIES_IDENTITIES_ALLOWED_TO_POST
              ) ||
            response?.[0].type ===
              errorAction(
                types.ADD_GROUP_MAIL_PROPERTIES_GROUPS_ALLOWED_TO_POST
              );

          if (isErrorAction) {
            return Promise.reject(
              new Error("Failed to update custom senders.")
            );
          }
          if (!suppressNotificationsAndRefresh) {
            fetchGroupMailProperties();
            showSuccessNotification(
              "Group Email Properties updated successfully!"
            );
          }
          return response;
        })
        .catch(() => {
          showErrorNotification("Failed to update custom senders.");
        });
    } else if (!suppressNotificationsAndRefresh) {
      showInfoNotification("No changes made to custom senders.");
    }
  };

  const dispatchCustomSendersRemove = async (
    { identities = [], groups = [] },
    suppressNotificationsAndRefresh = false
  ) => {
    const dispatchActions = [];
    if (identities.length) {
      dispatchActions.push(
        dispatch(
          deleteGroupMailPropertiesIdentitiesAllowedToPost(
            groupMailPropertiesId,
            identities
          )
        )
      );
    }
    if (groups.length) {
      dispatchActions.push(
        dispatch(
          deleteGroupMailPropertiesGroupsAllowedToPost(
            groupMailPropertiesId,
            groups
          )
        )
      );
    }
    if (dispatchActions.length) {
      setLoading(true);

      return Promise.all(dispatchActions)
        .then((response) => {
          const isErrorAction =
            response?.[0].type ===
              errorAction(
                types.DELETE_GROUP_MAIL_PROPERTIES_IDENTITIES_ALLOWED_TO_POST
              ) ||
            response?.[0].type ===
              errorAction(
                types.DELETE_GROUP_MAIL_PROPERTIES_GROUPS_ALLOWED_TO_POST
              );

          if (isErrorAction) {
            return Promise.reject(
              new Error("Failed to remove custom senders.")
            );
          }
          if (!suppressNotificationsAndRefresh) {
            fetchGroupMailProperties();
            showSuccessNotification(
              "Group Email Properties updated successfully!"
            );
          }
          return response;
        })
        .catch(() => {
          showErrorNotification("Failed to remove custom senders.");
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (!suppressNotificationsAndRefresh) {
      showInfoNotification("No changes made to custom senders.");
    }
  };

  const handleRestrictSendersSwitchChange = async (checked) => {
    if (checked) {
      await dispatchCustomSendersAdd(
        {
          identities: cachedRestrictSenders?.identitiesAllowedToPost?.map(
            (i) => i.id
          ),
          groups: cachedRestrictSenders?.groupsAllowedToPost?.map(
            (g) => g.groupIdentifier
          ),
        },
        true
      );
    } else {
      setCachedRestrictSenders(getCurrentAllSenders());
      await dispatchCustomSendersRemove(
        {
          identities: identitiesAllowedToPost.map((i) => i.id),
          groups: groupsAllowedToPost.map((g) => g.id),
        },
        true
      );
    }
    updateGroupMailProperties({ openPosting: !checked });
  };

  const handleGroupMembersCheckboxChange = (e) => {
    if (e.target.checked) {
      dispatchCustomSendersAdd({
        groups: [selectedGroup.groupIdentifier],
      });
    } else {
      if (selectedGroup.id !== selectedGroup.administratorsId) {
        dispatchCustomSendersRemove({ groups: [selectedGroup.id] });
      } else {
        showErrorNotification(
          "Group Members cannot be unchecked if the group is its own administrator.",
          ""
        );
      }
    }
  };

  const getRowKey = (customSender) => {
    if (customSender.type === "Group") {
      return "group-" + customSender.key;
    } else {
      return "identity-" + customSender.key;
    }
  };

  const handleAddModalOpen = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleCustomSendersCheckboxChange = (e) => {
    const checked = e.target.checked;
    if (checked) {
      if (
        cachedCustomSenders &&
        (cachedCustomSenders.identitiesAllowedToPost.length > 0 ||
          cachedCustomSenders.groupsAllowedToPost.length > 0)
      ) {
        dispatchCustomSendersAdd({
          identities: cachedCustomSenders.identitiesAllowedToPost.map(
            (i) => i.id
          ),
          groups: cachedCustomSenders.groupsAllowedToPost.map(
            (g) => g.groupIdentifier
          ),
        });
      } else {
        handleAddModalOpen();
      }
    } else {
      setCachedCustomSenders(getCurrentCustomSenders());
      dispatchCustomSendersRemove({
        identities: identitiesAllowedToPost.map((i) => i.id),
        groups: customGroupsAllowedToPost.map((g) => g.id),
      });
    }
  };

  const handleAddMember = (addedMember = {}) => {
    const { id, upn, unconfirmed, unconfirmedEmail, groupIdentifier } =
      addedMember;
    let identitiesToUpdate = [],
      groupsToUpdate = [];
    if (id && (upn || (unconfirmed && unconfirmedEmail))) {
      identitiesToUpdate.push(id);
    } else if (groupIdentifier) {
      groupsToUpdate.push(groupIdentifier);
    }
    dispatchCustomSendersAdd({
      identities: identitiesToUpdate,
      groups: groupsToUpdate,
    });
  };

  const handleCreateByEmail = async (email) => {
    try {
      setLoading(true);
      // Only add as a custom sender, NOT as a group member
      // this will create an identity object if it is not a
      // cern email. Adding CERN emails e.g. noreply@cern.ch will
      // fail if there is not a corresponding identity. An unconfirmed email with
      // that email should be created in case of issues
      await dispatchCustomSendersAdd({
        identities: [email],
      });
    } catch (error) {
      showErrorNotification(
        "Failed to add new external email to custom senders."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    const filteredIdentities = identitiesAllowedToPost
      ?.filter((identity) => selectedRows.includes(`identity-${identity.id}`))
      .map(
        (identity) =>
          `${identity.displayName} ${identity.upn ? `(${identity.upn})` : ""}`
      );
    const filteredGroups = groupsAllowedToPost
      ?.filter((group) => selectedRows.includes(`group-${group.id}`))
      .map((group) => group.groupIdentifier);
    setItemsToRemove([...filteredIdentities, ...filteredGroups]);
    setIsRemoveModalOpen(true);
  };

  const handleRemoveModalOk = () => {
    handleCustomSendersRemove();
    setIsRemoveModalOpen(false);
  };

  const handleRemoveModalCancel = () => {
    setIsRemoveModalOpen(false);
  };

  const handleCustomSendersRemove = async () => {
    setIsRemoving(true);
    const filterAndCleanItems = (items, prefix) => {
      return items
        ?.filter((item) => selectedRows.includes(`${prefix}-${item.id}`))
        .map((item) => item.id.replace(`${prefix}-`, ""));
    };
    const identitiesToRemove = filterAndCleanItems(
      identitiesAllowedToPost,
      "identity"
    );
    const groupsToRemove = filterAndCleanItems(groupsAllowedToPost, "group");
    dispatchCustomSendersRemove({
      identities: identitiesToRemove,
      groups: groupsToRemove,
    });
    setSelectedRows([]);
    setIsRemoving(false);
  };

  return (
    <>
      <Card
        style={{ marginBottom: 14 }}
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            <FileProtectOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
            Email posting restrictions
          </Typography.Title>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <LabeledSwitch
            checked={allowAuthenticatedMessagesOnly}
            disabled={!canModifyGroup}
            onChange={handleRestrictAuthenticatedSendersSwitchChange}
            label="Restrict to authenticated senders"
            labelContent={
              <TooltipIcon title="In addition to the posting restrictions below, only accept mails sent from a CERN mailbox (Outlook, Thunderbird etc.), and automatic messages from CERN services. Messages coming from the Internet will be refused." />
            }
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <LabeledSwitch
            checked={restrictSendersSwitchValue}
            disabled={!canModifyGroup}
            onChange={handleRestrictSendersSwitchChange}
            label="Restrict who can send emails to this group"
            labelContent={
              !restrictSendersSwitchValue ? (
                <TooltipIcon
                  title="Warning: This setting may make the group more susceptible to receiving spam or unwanted emails."
                  color="orange"
                />
              ) : null
            }
          />
        </div>
        {restrictSendersSwitchValue && (
          <div
            style={{
              marginLeft: 16,
              marginBottom: 16,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <Checkbox checked disabled>
              Owners/Admins of the group
            </Checkbox>
            <Checkbox
              checked={groupMembersCheckboxValue}
              disabled={groupMembersCheckboxState || !canModifyGroup}
              onChange={handleGroupMembersCheckboxChange}
            >
              Group Members
              {groupMembersCheckboxState && (
                <TooltipIcon title="Group Members cannot be unchecked if the group is its own administrator." />
              )}
            </Checkbox>
            <Checkbox
              checked={customSendersCheckboxValue}
              disabled={!canModifyGroup}
              onChange={handleCustomSendersCheckboxChange}
            >
              Custom senders
            </Checkbox>
          </div>
        )}
        {customSendersCheckboxValue && (
          <>
            <Flex justify="end" style={{ marginBottom: 24 }}>
              <AddMemberSearchModal
                title="Add Custom Senders"
                addButtonText="Add Custom Senders"
                searchValue=""
                members={dataSource}
                nonAddableMembers={nonAddableMembers}
                isReplicaGroup={!canModifyGroup}
                onSelect={handleAddMember}
                onCreateByEmail={handleCreateByEmail}
                isModalOpen={isAddModalOpen}
                onModalClose={handleAddModalClose}
                hideComment
                hideChangeMailingAddress
              />
            </Flex>
            <ContentTable
              rowKey={getRowKey}
              columns={tableColumns}
              dataSource={dataSource}
              rowSelection={rowSelection}
            />
            {dataSource.length > 0 && (
              <div style={{ marginTop: "-48px" }}>
                <Button
                  id="customSendersRemoveSelectionButton"
                  loading={isRemoving}
                  disabled={!hasSelections || !canModifyGroup}
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleRemove}
                />
              </div>
            )}
          </>
        )}
      </Card>
      <Modal
        title={
          <span>
            <DeleteOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
            Confirm Deletion
          </span>
        }
        centered
        open={isRemoveModalOpen}
        onCancel={handleRemoveModalCancel}
        onOk={handleRemoveModalOk}
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to remove the following custom senders?</p>
        <List
          dataSource={itemsToRemove}
          renderItem={(item) => <List.Item>{item}</List.Item>}
          bordered
          size="small"
        />
      </Modal>
    </>
  );
};

MailPostingRestrictions.propTypes = {
  groupMailPropertiesId: PropTypes.string,
  identitiesAllowedToPost: PropTypes.arrayOf(PropTypes.object),
  groupsAllowedToPost: PropTypes.arrayOf(PropTypes.object),
  selectedGroup: PropTypes.object,
  allowAuthenticatedMessagesOnly: PropTypes.bool,
  openPosting: PropTypes.bool.isRequired,
  canModifyGroup: PropTypes.bool,
  updateGroupMailProperties: PropTypes.func,
  fetchGroupMailProperties: PropTypes.func,
  setLoading: PropTypes.func,
};

export default MailPostingRestrictions;
